import { api } from './client'

/**
 * F-051 (REQ-114-001, REQ-113-001): consumer for `GET /api/work-queue/call-center`
 * (`WorkQueueController.GetCallCenter`). Read-only; no amounts anywhere in the response.
 */
export type CallCenterItemType =
  | 'lead_nuovo'
  | 'lead_richiamata_da_effettuare'
  | 'lead_ritentativo_dovuto'
  | 'lead_tentativi_esauriti'
  | 'task_da_completare'
  | 'appointment_da_confermare'

/** F-084 (REQ-068-001): stato operativo derivato del lead (non e' una tabella configurabile). */
export interface LeadOperationalState {
  code: string
  label: string
  /** 'Convertito' | 'Perso' | null se il lead e' aperto. */
  finalOutcome: string | null
  lostReason: string | null
}

export interface CallCenterItem {
  type: CallCenterItemType
  entityType: 'lead' | 'task' | 'appointment'
  entityId: number
  /** Id of the lead to open at `/leads/{id}`; null for a task with no visible lead. */
  leadId: number | null
  title: string
  /** 0 recent, 1 callback, 2 hot, 3 retry, 4 old, 5 exhausted (see `CallCenterQueueOrdering`). */
  rank: number
  /** F-084: punteggio del lead 0..100 (non e' una probabilita'); null se non calcolato. */
  score: number | null
  /** F-084: null per le voci senza lead visibile. */
  operationalState: LeadOperationalState | null
  leadName: string | null
  phone: string | null
  source: string | null
  sourceLabel: string | null
  campaign: string | null
  sector: string | null
  /** F-141 (REQ-113-001): il prodotto che la campagna promuove (testo della campagna), separato dal settore. */
  product?: string | null
  temperature: string | null
  attempts: number
  lastOutcomeCode: string | null
  lastOutcomeLabel: string | null
  lastAttemptAt: string | null
  dueAt: string | null
  createdAt: string
}

export interface CallCenterQueue {
  total: number
  hasMore: boolean
  count: number
  items: CallCenterItem[]
  /** The "Prossimo lead": null when there is no lead to work. */
  next: CallCenterItem | null
  provisional: {
    /** F-097: true finché nessuna politica di ritentativo è attiva (vale il piano predefinito, provvisorio). */
    retrySchedule: boolean
    /** Il massimo di tentativi del piano predefinito. */
    maxAttempts: number
    /** F-097: le politiche di ritentativo attive (una per campagna al massimo; campaignId null = predefinita). */
    retryPolicies?: { policyId: number; campaignId: number | null; maxAttempts: number; exhaustionAction: string }[]
    recentWindowHours: number
    /** F-084: finestra provvisoria per gli appuntamenti da confermare. */
    confirmWindowHours: number
  }
}

export const workQueueApi = {
  callCenter: (limit = 100) => api<CallCenterQueue>(`/api/work-queue/call-center?limit=${limit}`),
}

/**
 * F-057 (REQ-212-001, REQ-213-003, REQ-214-001): consumer for `GET /api/work-queue`, "La mia coda di
 * lavoro". Il server decide quali voci spettano al ruolo del chiamante; qui NON si legge mai
 * `economicValue` (il backend lo omette senza CanSeeRealAmounts e questa pagina non lo mostra comunque:
 * "il report comanda"), quindi il campo non e' nemmeno nel tipo.
 */
/** Scheda della pratica in coda (solo voci `contract`): quello che serve per capire la pratica senza aprirla. */
export interface WorkQueueContractCard {
  referenceCode: string
  status: string
  contactId: number | null
  companyId: number | null
  customerName: string | null
  operatorId: number | null
  operatorName: string | null
  productId: number | null
  productName: string | null
  mandanteId: number | null
  signedAt: string | null
  createdAt: string
  /** Giorni dall'ultimo cambio di stato. */
  waitingDays: number
  /** Perche' la pratica e' in coda. */
  reason: string
  /** Dati che mancano (cliente, operatore, prodotto, mandante, data firma). */
  missing: string[]
}

export interface WorkQueueItem {
  /** Presente solo per le voci di tipo pratica. */
  contract?: WorkQueueContractCard | null
  type: string
  entityType: string
  entityId: number
  title: string
  dueAt: string | null
  isStale: boolean
  score: number
  explanation: string[]
  sourceRole: string
  /** R3-07: presenti solo con il flag coda.fonti_estese. */
  needsHuman?: boolean
  /** R3-07: «si può automatizzare con…», solo con il flag coda.fonti_estese. */
  automationHint?: string | null
}

export interface WorkQueueResponse {
  roles: string[]
  type: string | null
  limit: number
  /** F-118: posizione della pagina e della prossima (null = non c'e' altro). */
  offset: number
  nextOffset: number | null
  total: number
  hasMore: boolean
  count: number
  items: WorkQueueItem[]
}

/**
 * F-153 (TASK-0407, REQ-214-001): i fattori di priorita' della coda oltre ai cinque di base
 * (`GET/PUT /api/work-queue/priority-weights`). Peso intero 0..100 per fattore; 0 = spento. Con tutti i pesi a 0 il
 * punteggio e' quello di sempre. La lettura e' dello staff, la scrittura solo dell'Admin. Nessun importo.
 */
export interface PriorityFactor {
  key: string
  label: string
  description: string
  /** 0 = spento. */
  weight: number
}

export interface PriorityWeights {
  factors: PriorityFactor[]
  preferred: { sectors: string[]; operators: string[]; products: string[]; segments: string[] }
  maxWeight: number
  baseComponents: { key: string; weight: number }[]
  formula: string
}

export interface PriorityWeightsUpdate {
  /** Fattore -> peso intero 0..100. */
  weights?: Record<string, number>
  /** Lista ('sectors' | 'operators' | 'products' | 'segments') -> valori separati da virgole. */
  preferred?: Record<string, string>
}

/** F-153 (REQ-213-002): lead mai contattato oltre il tempo di risposta; solo con WorkQueue:CallCenter:ResponseTimeItems. */
export const RESPONSE_TIME_ITEM_TYPE = 'lead_fuori_tempo_risposta'

export const priorityWeightsApi = {
  get: () => api<PriorityWeights>('/api/work-queue/priority-weights'),
  update: (body: PriorityWeightsUpdate) =>
    api<PriorityWeights>('/api/work-queue/priority-weights', { method: 'PUT', body: JSON.stringify(body) }),
}

export const myWorkQueueApi = {
  list: (type?: string, limit = 200, sourceRole?: string, offset = 0) => {
    const qs = new URLSearchParams({ limit: String(limit) })
    // F-118: paginazione; l'ordine e' lo stesso a ogni pagina.
    if (offset > 0) qs.set('offset', String(offset))
    if (type) qs.set('type', type)
    // F-106: filtro per ruolo d'origine della voce.
    if (sourceRole) qs.set('sourceRole', sourceRole)
    return api<WorkQueueResponse>(`/api/work-queue?${qs.toString()}`)
  },
}
